import React, { useEffect, useState } from 'react';
import { GitBranch, Target, User, Users } from 'lucide-react';
import { useUserStore } from '../../lib/stores/userStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { SearchableSelect } from '../ui/searchable-select';
import { ActivityNoteDrawer } from '../shared/activity-drawer/ActivityNoteDrawer';
import { PropRow } from '../shared/activity-drawer/ui/PropRow';
import { SectionToggle } from '../shared/activity-drawer/ui/SectionToggle';
import { useLeadDrawerAdapter } from './leadDrawerAdapter';
import type { LeadStage } from '../../lib/types';

interface LeadStatusUpdateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentStage: string;
  leadId: string;
  currentAssignee?: string;
  currentSalesRepId?: string; // Commerciale assegnato attuale
  leadStages: LeadStage[];
  leadPipelineId?: string | null; // ID della pipeline del lead (null per legacy)
  onStageUpdated: () => void;
  onNotesUpdated: () => void;
  onAssigneeUpdated?: () => void;
  onActivityAdded?: () => void;
  onActivityUpdated?: () => void;
  translations: any;
  defaultTab?: 'note' | 'activity';
  requireNote?: boolean; // Forza la nota obbligatoria (drag&drop Kanban)
  onNoteRequired?: () => void; // Accettata per compatibilità: la shell toasta internamente
  editingActivity?: any; // Attività in modifica
  initialActivityData?: { title: string; type: string; description: string; due_date?: string; due_time?: string } | null; // Pre-fill suggerito dall'AI (o escalation dal composer inline)
  /** Pre-fill dell'editor nel tab Nota (escalation dal composer inline della cronologia). */
  initialNoteText?: string | null;
  pipelineContext?: string; // Pipeline corrente per scoping note/attività
  /**
   * Apre lo slot "Fase lead" già espanso quando il drawer viene mostrato per un cambio
   * fase esplicito (Task 7 redesign, eccezione dichiarata 2). Ignorato quando `requireNote`
   * è true: in quel flusso lo slot è sempre il callout pinned, mai richiudibile.
   */
  defaultStatusOpen?: boolean;
  /**
   * SOLO Kanban/`requireNote`: id della fase di PARTENZA (prima del drag), per la riga di
   * transizione "FaseA → FaseB" del callout pinned. `currentStage` in quel flusso porta già
   * la fase di ARRIVO (convenzione storica: inizializza `newStage`), quindi la fase di
   * partenza non è altrimenti ricavabile dalle props esistenti. Assente altrove: la riga di
   * transizione degrada mostrando solo la fase di arrivo.
   */
  priorStageId?: string;
  /** Motivo della perdita gia' scelto all'apertura (es. proposto dall'assistente della chat). */
  initialLostReason?: string | null;
}

const DEFAULT_LOST_REASONS = [
  'Budget insufficiente',
  'Scelto competitor',
  'Tempistiche non compatibili',
  'Non risponde',
  'Non in target',
  'Spam / Lead non valido',
  'Altro',
];

/** Iniziali per il mini-avatar dell'assegnatario (mockup `.sc-mini-avatar`), stesso helper di `ActivityTab.tsx`. */
function getInitials(user?: { full_name?: string | null; email?: string | null } | null): string {
  const source = user?.full_name?.trim() || user?.email || '';
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function MiniAvatar({ user }: { user?: { full_name?: string | null; email?: string | null } | null }) {
  return (
    <span className="w-4 h-4 rounded-full bg-[#DDEDFB] dark:bg-[rgba(35,131,226,0.25)] text-[#1D6FBF] dark:text-[#7CB8EE] text-[8px] font-semibold inline-flex items-center justify-center shrink-0">
      {getInitials(user)}
    </span>
  );
}

function StageDot({ color }: { color?: string }) {
  return <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color || '#9B9A97' }} />;
}

/**
 * Drawer Nota/Attività di un lead: thin wrapper sul drawer condiviso
 * (`shared/activity-drawer/ActivityNoteDrawer`). Qui vive SOLO ciò che è specifico
 * dell'entità lead — lo slot di stato (fase, motivo perso, assegnatario, commerciale)
 * e l'adapter con le sue scritture (`leadDrawerAdapter.ts`). Editor, tab, date,
 * calendari Google/Microsoft, attendee, Meet/Teams e AlfaDocs sono TUTTI nella shell.
 *
 * Props pubbliche invariate salvo l'aggiunta di `defaultStatusOpen`/`priorStageId`
 * (Task 7 redesign — solo presentazione): `LeadDetailsPage` e `LeadKanban` non cambiano
 * import né le prop esistenti.
 *
 * Redesign T7: lo slot è un `SectionToggle` ("Fase lead", chiuso di default, sunto con
 * pallino colore + mini-avatar assegnatario) con dentro `PropRow` per fase/assegnatario/
 * commerciale/motivo perdita. Con `requireNote` (SOLO Kanban, M7) diventa un callout
 * pinned non richiudibile con la transizione "FaseA → FaseB" in testa (mockup vista 3).
 * Fonte pre-redesign: versione precedente di questo file, :119-224.
 */
const LeadStatusUpdateDrawer: React.FC<LeadStatusUpdateDrawerProps> = ({
  isOpen,
  onClose,
  currentStage,
  leadId,
  currentAssignee,
  currentSalesRepId,
  leadStages,
  leadPipelineId,
  onStageUpdated,
  onNotesUpdated,
  onAssigneeUpdated,
  onActivityAdded,
  onActivityUpdated,
  translations: t,
  defaultTab = 'note',
  requireNote = false,
  onNoteRequired: _onNoteRequired,
  editingActivity,
  initialActivityData,
  initialNoteText,
  pipelineContext,
  defaultStatusOpen = false,
  priorStageId,
  initialLostReason,
}) => {
  const [newStage, setNewStage] = useState(currentStage);
  const [newAssignee, setNewAssignee] = useState(currentAssignee || '');
  const [lostReason, setLostReason] = useState(initialLostReason || '');
  const [invitedCommercial, setInvitedCommercial] = useState(currentSalesRepId || '');
  const [statusOpen, setStatusOpen] = useState(defaultStatusOpen);

  const { users } = useUserStore();
  const { settings } = useSettingsStore();

  const selectedStageIsLost = leadStages.find((s) => s.id === newStage)?.is_lost === true;
  const lostReasons =
    settings?.lost_reasons && settings.lost_reasons.length > 0 ? settings.lost_reasons : DEFAULT_LOST_REASONS;

  // Allineamento alle props (fonte :741-752) + reset all'apertura (fonte :762-769).
  useEffect(() => setNewStage(currentStage), [currentStage]);
  useEffect(() => setNewAssignee(currentAssignee || ''), [currentAssignee]);
  useEffect(() => setInvitedCommercial(currentSalesRepId || ''), [currentSalesRepId]);
  useEffect(() => {
    if (!isOpen) return;
    setNewStage(currentStage);
    setNewAssignee(currentAssignee || '');
    setLostReason(initialLostReason || '');
    setInvitedCommercial(currentSalesRepId || '');
    setStatusOpen(defaultStatusOpen);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const { adapter, handleNotesUpdated } = useLeadDrawerAdapter({
    isOpen,
    leadId,
    currentStage,
    currentAssignee,
    currentSalesRepId,
    leadPipelineId,
    pipelineContext,
    requireNote,
    newStage,
    newAssignee,
    lostReason,
    invitedCommercial,
    selectedStageIsLost,
    onStageUpdated,
    onNotesUpdated,
    onAssigneeUpdated,
  });

  const currentStageMeta = leadStages.find((s) => s.id === currentStage);
  const newStageMeta = leadStages.find((s) => s.id === newStage);
  const assigneeUser = users.find((u) => u.id === currentAssignee);
  const statusModified =
    newStage !== currentStage ||
    newAssignee !== (currentAssignee || '') ||
    invitedCommercial !== (currentSalesRepId || '');

  const statusProps = (
    <>
      <PropRow icon={<GitBranch className="h-3 w-3" strokeWidth={1.5} />} label="Nuova fase" wrap>
        <StageDot color={newStageMeta?.color} />
        <SearchableSelect
          value={newStage}
          onValueChange={(value) => setNewStage(value)}
          options={leadStages.map((stage) => ({ value: stage.id, label: stage.name }))}
          placeholder="Seleziona fase..."
          ariaLabel="Nuova fase"
          className="flex-1 min-w-0"
          triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
        />
      </PropRow>

      <PropRow icon={<User className="h-3 w-3" strokeWidth={1.5} />} label="Assegnatario lead" wrap>
        {newAssignee && <MiniAvatar user={users.find((u) => u.id === newAssignee)} />}
        <SearchableSelect
          value={newAssignee}
          onValueChange={(value) => setNewAssignee(value)}
          options={[
            { value: '', label: 'Non assegnato' },
            ...users.map((user) => ({
              value: user.id,
              label: user.full_name || user.email || user.id,
            })),
          ]}
          placeholder="Non assegnato"
          ariaLabel="Assegnatario lead"
          className="flex-1 min-w-0"
          triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
        />
      </PropRow>

      <PropRow icon={<Users className="h-3 w-3" strokeWidth={1.5} />} label="Commerciale" wrap>
        <SearchableSelect
          value={invitedCommercial}
          onValueChange={(value) => setInvitedCommercial(value)}
          options={[
            { value: '', label: 'Nessuno' },
            ...users.map((user) => ({
              value: user.id,
              label: user.full_name || user.email || user.id,
            })),
          ]}
          placeholder="Seleziona commerciale..."
          ariaLabel="Commerciale"
          className="flex-1 min-w-0"
          triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
        />
      </PropRow>

      {selectedStageIsLost && (
        <PropRow icon={<Target className="h-3 w-3" strokeWidth={1.5} />} label="Motivo della perdita" required danger wrap>
          <SearchableSelect
            value={lostReason}
            onValueChange={(value) => setLostReason(value)}
            options={lostReasons.map((reason) => ({ value: reason, label: reason }))}
            placeholder="Seleziona motivo..."
            ariaLabel="Motivo della perdita"
            className="flex-1 min-w-0"
            triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
          />
        </PropRow>
      )}
    </>
  );

  let statusSection: React.ReactNode;

  if (requireNote) {
    // Fonte mockup vista 3: callout pinned non richiudibile, con la transizione di fase in
    // testa. `priorStageId` è opzionale (SOLO Kanban lo passa): se assente si mostra solo la
    // fase di arrivo, senza rompere il rendering (M7 — requireNote esiste solo lato lead).
    const priorStageMeta = priorStageId ? leadStages.find((s) => s.id === priorStageId) : undefined;
    statusSection = (
      <div className="bg-[#F7F7F5] dark:bg-[#373C3F] rounded-md p-1">
        <div className="flex items-center gap-2 px-2 py-1.5">
          <GitBranch className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
          <span className="text-[11px] font-medium uppercase tracking-[0.05em] text-[rgba(55,53,47,0.5)] dark:text-white/50">
            Fase lead
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#37352F] dark:text-white">
            {priorStageMeta && (
              <>
                <StageDot color={priorStageMeta.color} />
                {priorStageMeta.name}
                <span className="text-[#787774] dark:text-gray-400">→</span>
              </>
            )}
            <StageDot color={newStageMeta?.color} />
            {newStageMeta?.name || newStage}
          </span>
        </div>
        <div className="pt-1.5 pb-1 flex flex-col gap-2">
          {statusProps}
          <div className="text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
            Le modifiche vengono salvate insieme alla nota.
          </div>
        </div>
      </div>
    );
  } else {
    statusSection = (
      <SectionToggle
        open={statusOpen}
        onToggle={() => setStatusOpen((prev) => !prev)}
        icon={<GitBranch className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />}
        title="Fase lead"
        dataTour="drawer-status-toggle"
        summary={
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#37352F] dark:text-white">
            <StageDot color={currentStageMeta?.color} />
            {currentStageMeta?.name || currentStage}
            {currentAssignee && <MiniAvatar user={assigneeUser} />}
          </span>
        }
        trailing={
          statusModified ? (
            <span className="text-[11px] font-medium text-[#2383E2]">Modificato</span>
          ) : (
            <span className="text-[11px] font-medium text-[#787774] dark:text-gray-400">Modifica</span>
          )
        }
      >
        {statusProps}
        <div className="text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
          Le modifiche vengono salvate insieme alla nota o all'attività.
        </div>
      </SectionToggle>
    );
  }

  return (
    <ActivityNoteDrawer
      isOpen={isOpen}
      onClose={onClose}
      adapter={adapter}
      defaultTab={defaultTab}
      requireNote={requireNote}
      editingActivity={editingActivity}
      initialActivityData={initialActivityData}
      initialNoteText={initialNoteText}
      statusSection={statusSection}
      translations={t}
      onNotesUpdated={handleNotesUpdated}
      onActivityAdded={onActivityAdded}
      onActivityUpdated={onActivityUpdated}
    />
  );
};

export default LeadStatusUpdateDrawer;
